import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import supabase, { getSessionSafe } from '../../utils/supabase-client';
import { prefetchCSRFToken } from '../../utils/axios-auth';
import { tryInitSentryWithCurrentConsent } from '../../utils/sentry-init.js';
import { store } from '../../redux/store';
import { setUser, fetch_user_profile } from '../../redux/auth-slice';
import { clear_user } from '../../redux/user-slice';
import { useNavigate, useLocation } from 'react-router-dom';
import { debugSessionLog } from '../../utils/debug-session-log';
/**
 * Component that initializes the user's session and profile when the app loads.
 * Checks Supabase auth state, updates Redux auth state, and fetches profile data.
 */
const ProfileInitializer = () => {
    const dispatch = useDispatch();
    const reduxSessionToken = useSelector((state) => state.auth.session?.access_token);
    const navigate = useNavigate();
    const location = useLocation();
    // Helper function to fetch profile only if session is valid and contains necessary info
    const fetchProfileIfNeeded = async (session) => {
        const reduxTokenNow = !!store.getState().auth.session?.access_token;
        // #region agent log
        debugSessionLog({ runId: 'post-fix', hypothesisId: 'H3', location: 'profile-initializer.tsx:fetchProfileIfNeeded:entry', message: 'fetchProfileIfNeeded entry', data: { hasSession: !!session, hasUserId: !!session?.user?.id, metaRole: session?.user?.user_metadata?.role ?? null, reduxHadToken: reduxTokenNow } });
        // #endregion
        // Signed out or broken session: clear stale Redux only when we still hold tokens
        if (!session?.user?.id) {
            if (reduxTokenNow) {
                // #region agent log
                debugSessionLog({ runId: 'post-fix', hypothesisId: 'H3', location: 'profile-initializer.tsx:fetchProfileIfNeeded:noUserId', message: 'clearing Redux — session has no user id', data: { reduxHadToken: true } });
                // #endregion
                dispatch(setUser({ user: null, session: null }));
                dispatch(clear_user());
            }
            return;
        }
        // Match AuthCallback: metadata role is not authoritative — detect from DB when missing / generic
        let userRole = session.user.user_metadata?.role;
        if ((!userRole || userRole === 'authenticated') && session.user.app_metadata?.role === 'admin') {
            userRole = 'admin';
        }
        if (!userRole || userRole === 'authenticated') {
            console.log('[ProfileInitializer] Role detection needed. Checking database...');
            try {
                const { data: tutorData } = await supabase.rpc('get_user_profile_by_id', {
                    user_id: session.user.id,
                    user_role: 'tutor',
                });
                if (tutorData) {
                    userRole = 'tutor';
                }
                else {
                    const { data: studentData } = await supabase.rpc('get_user_profile_by_id', {
                        user_id: session.user.id,
                        user_role: 'student',
                    });
                    if (studentData) {
                        userRole = 'student';
                    }
                }
            }
            catch (detectError) {
                console.error('[ProfileInitializer] Error detecting role:', detectError);
            }
        }
        if (!userRole || userRole === 'authenticated') {
            const currentUser = store.getState().auth.user;
            if (currentUser?.id === session.user.id && currentUser.role && currentUser.role !== 'authenticated') {
                userRole = currentUser.role;
                console.log('[ProfileInitializer] Using persisted Redux role:', userRole);
            }
            else {
                console.warn('[ProfileInitializer] Role detection failed, defaulting to student');
                userRole = 'student';
            }
        }
        // #region agent log
        debugSessionLog({ runId: 'post-fix', hypothesisId: 'H3', location: 'profile-initializer.tsx:fetchProfileIfNeeded:resolved', message: 'resolved role for session', data: { resolvedRole: userRole, uidPrefix: String(session.user.id).slice(0, 8) } });
        // #endregion
        console.log('[ProfileInitializer] fetchProfileIfNeeded:', session.user.id?.slice(0, 8), 'role:', userRole);
        const fetchArgs = {
            userId: session.user.id,
            userRole,
            userEmail: session.user.email ?? null,
        };
        const minimalUser = {
            id: session.user.id,
            email: session.user.email,
            role: userRole,
        };
        const sessionPayload = {
            access_token: session.access_token,
            refresh_token: session.refresh_token,
        };
        try {
            const currentUser = store.getState().auth.user;
            const hasFullProfile = currentUser && (currentUser.first_name || currentUser.last_name);
            const userToSet = hasFullProfile && currentUser?.id === session.user.id ? currentUser : minimalUser;
            await dispatch(setUser({ user: userToSet, session: sessionPayload })).unwrap();
            await prefetchCSRFToken();
            try {
                await dispatch(fetch_user_profile(fetchArgs)).unwrap();
                tryInitSentryWithCurrentConsent(store.getState().auth.user);
            }
            catch (profileError) {
                console.warn('ProfileInitializer: Could not fetch full profile, preserving existing user data:', profileError);
            }
            if (location.state?.profileJustCompleted) {
                navigate(location.pathname, { replace: true, state: {} });
                const targetDashboard = fetchArgs.userRole === 'student' ? '/student-dashboard' : '/tutor-dashboard';
                navigate(targetDashboard, { replace: true });
            }
        }
        catch (error) {
            console.error('ProfileInitializer: Error setting user data:', error);
        }
    };
    useEffect(() => {
        const initializeSession = async () => {
            try {
                // Use the timeout-safe wrapper so navigator.locks contention cannot
                // hang the boot sequence indefinitely. If the lock is wedged we get
                // back the localStorage-stored tokens (still valid for ~15min until
                // the next refresh) and the app continues to render with data.
                const safeSession = await getSessionSafe();
                // We still need the full Supabase session object (with `user`) for
                // role/profile detection. If safeSession is non-null, the lock has
                // already been released and a follow-up getSession() will resolve
                // immediately. If safeSession is null, we have neither stored
                // tokens nor a live session — there is nothing to initialize.
                let session = null;
                if (safeSession) {
                    try {
                        const { data, error } = await supabase.auth.getSession();
                        if (error)
                            throw error;
                        session = data?.session ?? null;
                    }
                    catch (err) {
                        // Fall back to a minimal session shape from localStorage tokens
                        // so downstream code can still detect role + fetch profile.
                        console.warn('[ProfileInitializer] follow-up getSession failed, using stored tokens', err);
                        try {
                            const { data } = await supabase.auth.getUser(safeSession.access_token);
                            if (data?.user) {
                                session = {
                                    user: data.user,
                                    access_token: safeSession.access_token,
                                    refresh_token: safeSession.refresh_token,
                                };
                            }
                        }
                        catch (userErr) {
                            console.warn('[ProfileInitializer] getUser fallback failed', userErr);
                        }
                    }
                }
                console.log('[ProfileInitializer] getSession on mount:', !!session);
                await fetchProfileIfNeeded(session);
            }
            catch (error) {
                console.error('ProfileInitializer: Error during initialization check:', error);
                if (reduxSessionToken) { // Only clear if state might be outdated
                    dispatch(setUser({ user: null, session: null }));
                    dispatch(clear_user());
                }
            }
        };
        initializeSession();
        // Set up a listener for auth state changes
        const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
            switch (_event) {
                case 'SIGNED_IN':
                case 'TOKEN_REFRESHED':
                case 'USER_UPDATED':
                    console.log('[ProfileInitializer] authStateChange:', _event, 'session:', !!session);
                    // Fetch profile based on the new/updated session data
                    await fetchProfileIfNeeded(session);
                    break;
                case 'SIGNED_OUT':
                    dispatch(setUser({ user: null, session: null }));
                    dispatch(clear_user());
                    // Optional navigation on logout
                    // if (!['/', '/login', '/signup'].includes(location.pathname)) {
                    //    navigate('/login', { replace: true });
                    // }
                    break;
                default:
                // Ignore other events like PASSWORD_RECOVERY, USER_DELETED etc.
            }
        });
        // Cleanup subscription on unmount
        return () => {
            authListener?.subscription.unsubscribe();
        };
        // Run on mount; auth state changes handled by onAuthStateChange listener
    }, [dispatch]);
    // This component doesn't render anything
    return null;
};
export default ProfileInitializer;
